import React, { useState, useRef, useCallback } from 'react';
import { Play, Download, Trash2, Clock, Film, X, Loader2, AlertCircle } from 'lucide-react';
import { useSlotScreenGeneratorStore } from '@store/slotScreenGeneratorStore';
import type { VideoGenerationHistoryEntry } from '@core/types/slot-screen-generator.types';

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60).toString().padStart(2, '0');
  const s = (seconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

function HistoryCard({
  entry,
  onPlay,
  onDelete,
}: {
  entry: VideoGenerationHistoryEntry;
  onPlay: (entry: VideoGenerationHistoryEntry) => void;
  onDelete: (id: string) => void;
}) {
  return (
    <div className="flex-shrink-0 w-[120px] rounded-md border border-zinc-700 bg-zinc-800 overflow-hidden group relative">
      {/* Thumbnail / status area */}
      <div
        className="relative h-[80px] bg-zinc-900 flex items-center justify-center cursor-pointer"
        onClick={() => entry.status === 'ready' && onPlay(entry)}
      >
        {entry.thumbnailUrl ? (
          <img src={entry.thumbnailUrl} alt="video thumbnail" className="w-full h-full object-cover" />
        ) : entry.status === 'generating' ? (
          <Loader2 size={20} className="text-yellow-500 animate-spin" />
        ) : entry.status === 'error' ? (
          <AlertCircle size={20} className="text-red-400" />
        ) : (
          <Film size={20} className="text-zinc-600" />
        )}

        {/* Play overlay */}
        {entry.status === 'ready' && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
              <Play size={14} className="text-white ml-0.5" />
            </div>
          </div>
        )}

        {/* Delete button */}
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(entry.id); }}
          className="absolute top-1 right-1 p-0.5 bg-black/60 rounded opacity-0 group-hover:opacity-100 hover:bg-red-900/80 transition-all"
          title="Delete"
        >
          <Trash2 size={10} className="text-zinc-300" />
        </button>
      </div>

      {/* Info row */}
      <div className="px-2 py-1.5 flex items-center justify-between">
        <span className="text-[10px] text-zinc-300 font-medium tabular-nums">
          {formatDuration(entry.duration)}
        </span>
        <span className="text-[9px] text-zinc-600 tabular-nums">
          {entry.resolution.width}×{entry.resolution.height}
        </span>
      </div>

      {/* Error message */}
      {entry.status === 'error' && entry.error && (
        <div className="px-2 pb-1">
          <p className="text-[9px] text-red-400 line-clamp-2" title={entry.error}>{entry.error}</p>
        </div>
      )}
    </div>
  );
}

export function VideoHistoryStrip() {
  const videoHistory = useSlotScreenGeneratorStore(s => s.videoHistory);
  const removeVideoHistoryEntry = useSlotScreenGeneratorStore(s => s.removeVideoHistoryEntry);

  const [playingEntry, setPlayingEntry] = useState<VideoGenerationHistoryEntry | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  const handlePlay = useCallback((entry: VideoGenerationHistoryEntry) => {
    setPlayingEntry(entry);
  }, []);

  const handleClosePlayer = useCallback(() => {
    setPlayingEntry(null);
  }, []);

  const handleDownload = useCallback((entry: VideoGenerationHistoryEntry) => {
    if (!entry.videoUrl) return;
    const a = document.createElement('a');
    a.href = entry.videoUrl;
    a.download = `slot-video-${formatDuration(entry.duration).replace(':', 'm')}s.mp4`;
    a.click();
  }, []);

  if (videoHistory.length === 0) return null;

  return (
    <>
      {/* History strip */}
      <div className="flex-shrink-0 border-t border-zinc-700 bg-zinc-900/80">
        <div className="flex items-center gap-1 px-2 py-2">
          <Clock size={13} className="text-zinc-500" />
          <span className="text-xs font-medium text-zinc-500 uppercase">Video History</span>
          <span className="text-xs font-medium text-zinc-600">{videoHistory.length}</span>
        </div>
        <div className="flex gap-2 px-2 pb-2 overflow-x-auto">
          {videoHistory.map(entry => (
            <HistoryCard
              key={entry.id}
              entry={entry}
              onPlay={handlePlay}
              onDelete={removeVideoHistoryEntry}
            />
          ))}
        </div>
      </div>

      {/* Video player modal */}
      {playingEntry && (
        <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center" onClick={handleClosePlayer}>
          <div
            className="relative bg-zinc-900 rounded-xl overflow-hidden shadow-2xl max-w-2xl w-full mx-4"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-zinc-700">
              <div className="flex items-center gap-2">
                <Film size={14} className="text-zinc-400" />
                <span className="text-sm text-zinc-300 font-medium">
                  {formatDuration(playingEntry.duration)} — {playingEntry.resolution.width}×{playingEntry.resolution.height}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleDownload(playingEntry)}
                  className="flex items-center gap-1.5 px-2.5 py-1 text-xs text-zinc-300 border border-zinc-600 rounded hover:bg-zinc-800 transition-colors"
                >
                  <Download size={11} />
                  Download
                </button>
                <button
                  onClick={handleClosePlayer}
                  className="p-1 hover:bg-zinc-800 rounded transition-colors"
                >
                  <X size={14} className="text-zinc-400" />
                </button>
              </div>
            </div>

            {/* Video */}
            <div className="bg-black">
              <video
                ref={videoRef}
                src={playingEntry.videoUrl}
                controls
                autoPlay
                className="w-full max-h-[70vh] object-contain"
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
